/*
 * Mapbox requires WebGL to work, but it appears that this api is not present
 * on some computers. This problem affects many browsers. According to the doc:
 *
 * "Support for WebGL is present in Firefox 4+, Google Chrome 9+, Opera 12+,
 * Safari 5.1+, Internet Explorer 11+, and Microsoft Edge build 10240+;
 * however, the user's device must also have hardware that supports these
 * features."
 *
 * For the moment we just make sure that we do not crash and display a dummy
 * background. In the future we could develop a more sophisticated fallback.
 */
import { Map as MapboxMapInstance } from 'mapbox-gl';
import * as React from 'react';

import { useLogPosthogEvent } from '~src/commons/services/posthog-service';

import { CategoryOption } from '../../api/api';
import { useCategoryState } from '../category/category-state';
import { useGlobalState } from '../global-state';
import { ListState } from '../ui/list-state';
import { MapboxMap, MapboxMarker, fitBounds } from './mapbox-react';
import { PinpointMarker } from './pinpoint-marker';
import { PositionMarker } from './position-marker';

const savedZoom = new Map();

const DEFAULT_ZOOM = 14;

interface LocationMapProps {
  listState: ListState<CategoryOption>;
}

export const LocationMap: React.FC<LocationMapProps> = ({ listState }) => {
  const globalState = useGlobalState();
  const category = useCategoryState();
  const mapRef = React.useRef<MapboxMapInstance>();
  const logPosthogEvent = useLogPosthogEvent();

  const selectedItem = listState.getSelectedItem();
  const onSelect = (option: CategoryOption) => listState.setSelectedKey(option.location!.id);

  const markers = category.options.map((option: CategoryOption) => {
    const selected = option.location!.id === listState.selectedKey;
    return (
      <MapboxMarker
        key={option.location!.id}
        anchor="bottom"
        coordinates={option.location!.address.coordinates!}
        mapRef={mapRef}
        zIndex={selected ? '1000' : undefined}
        onClick={() => {
          onSelect(option);
          logPosthogEvent('Pickup Location Pin Clicked', {
            name: option.location!.name,
            price: option.price.amount,
            type: option.location!.type,
            distance_in_meters: option.location?.distance?.meter,
          });
        }}
      >
        <PinpointMarker name={option.location!.name} selected={selected} />
      </MapboxMarker>
    );
  });

  function onInit() {
    if (savedZoom.get(category.id) || category.options.length < 2) {
      return;
    }

    const coordinatesArray = category.options.map((option: CategoryOption) => {
      return option.location!.address.coordinates!;
    });

    if (globalState.search_address?.coordinates) {
      coordinatesArray.push(globalState.search_address?.coordinates);
    }

    fitBounds(mapRef.current!, coordinatesArray);
  }

  return (
    <MapboxMap
      center={selectedItem?.location!.address.coordinates}
      defaultZoom={savedZoom.get(category.id) ?? DEFAULT_ZOOM}
      mapRef={mapRef}
      onInit={onInit}
      onZoom={(newZoom: number) => savedZoom.set(category.id, newZoom)}
    >
      {markers}
      {globalState.search_address?.coordinates && (
        <MapboxMarker coordinates={globalState.search_address?.coordinates} mapRef={mapRef}>
          <PositionMarker />
        </MapboxMarker>
      )}
    </MapboxMap>
  );
};
